import { z } from 'zod';
/**
 * Validation de la configuration au démarrage (§7 bis.2).
 *
 * Si une variable manque ou est mal formée, le service refuse de démarrer avec
 * un message explicite. Aucun repli silencieux, aucune valeur par défaut en
 * dehors de ENVIRONMENT=local.
 */
const urlAbsolue = z
    .string()
    .min(1)
    .refine((v) => /^[a-z][a-z0-9+.-]*:\/\//i.test(v), {
    message: 'doit être une URL absolue, schéma compris',
})
    .transform((v) => v.replace(/\/+$/, ''));
const environnement = z.enum(['local', 'staging', 'production']);
const langue = z.enum(['fr', 'ar', 'en']);
/** Variables communes à tous les services. */
const socle = {
    BASE_DOMAIN: z.string().min(3).regex(/^[a-z0-9.-]+$/, 'nom de domaine sans schéma ni slash'),
    ENVIRONMENT: environnement,
    DEFAULT_LOCALE: langue,
    SUPPORTED_LOCALES: z
        .string()
        .default('fr')
        .transform((v) => v.split(',').map((s) => s.trim()).filter(Boolean))
        .pipe(z.array(langue).nonempty()),
    CURRENCY: z.literal('MAD'),
    TIMEZONE: z.string().min(3),
    LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
};
/** URLs publiques — les seules qui peuvent atteindre le navigateur. */
const urlsPubliques = {
    PUBLIC_WEB_URL: urlAbsolue,
    PUBLIC_APP_URL: urlAbsolue,
    PUBLIC_API_URL: urlAbsolue,
    PUBLIC_AUTH_URL: urlAbsolue,
    OIDC_ISSUER_URL: urlAbsolue,
    OIDC_REDIRECT_URI: urlAbsolue,
};
/**
 * Configuration exposée au navigateur. Tout ce qui est ici est public par
 * construction : n'y mettre aucun secret (§7 bis.2).
 */
export const schemaEnvPublic = z.object({
    ...socle,
    ...urlsPubliques,
    OIDC_REALM: z.string().min(1),
    OIDC_CLIENT_ID_APP: z.string().min(1),
});
/** Configuration du BFF. Contient les secrets : ne sort jamais du serveur. */
export const schemaEnvServeur = z
    .object({
    ...socle,
    ...urlsPubliques,
    PORT: z.coerce.number().int().positive().default(3000),
    // Briques internes — jamais transmises au navigateur.
    // Pas d'URL de moteur de réservation : la réservation est tenue par le BFF
    // lui-même, sur PostgreSQL (ADR 0004).
    CMS_INTERNAL_URL: urlAbsolue,
    CRM_INTERNAL_URL: urlAbsolue,
    LMS_INTERNAL_URL: urlAbsolue,
    AUTH_INTERNAL_URL: urlAbsolue,
    S3_INTERNAL_ENDPOINT: urlAbsolue,
    DATABASE_URL: z.string().startsWith('postgresql://'),
    REDIS_URL: z.string().startsWith('redis://'),
    S3_REGION: z.string().min(1),
    S3_BUCKET_DOCUMENTS: z.string().min(1),
    S3_BUCKET_FACTURES: z.string().min(1),
    S3_BUCKET_MEDIAS: z.string().min(1),
    S3_ACCESS_KEY: z.string().min(1),
    S3_SECRET_KEY: z.string().min(1),
    S3_SIGNED_URL_TTL_SECONDS: z.coerce.number().int().positive().default(300),
    OIDC_REALM: z.string().min(1),
    OIDC_CLIENT_ID_APP: z.string().min(1),
    OIDC_CLIENT_ID_BFF: z.string().min(1),
    OIDC_CLIENT_SECRET: z.string().min(1),
    CMS_STATIC_TOKEN: z.string().min(1),
    CRM_API_KEY: z.string().min(1),
    SMTP_HOST: z.string().min(1),
    SMTP_PORT: z.coerce.number().int().positive(),
    SMTP_SECURE: z.enum(['true', 'false']).transform((v) => v === 'true'),
    SMTP_USER: z.string().optional(),
    SMTP_PASSWORD: z.string().optional(),
    MAIL_FROM_ADDRESS: z.string().email(),
    MAIL_FROM_NAME: z.string().min(1),
    MAIL_REPLY_TO: z.string().email(),
    PAYMENT_PROVIDER: z.enum(['virement', 'cmi', 'youcanpay', 'payzone', 'stripe']),
    PAYMENT_MERCHANT_ID: z.string().optional(),
    PAYMENT_SECRET_KEY: z.string().optional(),
    PAYMENT_RETURN_URL: urlAbsolue,
    PAYMENT_CANCEL_URL: urlAbsolue,
    PAYMENT_WEBHOOK_URL: urlAbsolue,
    FORM_TOKEN_SECRET: z.string().min(16, 'au moins 16 caractères'),
    OTEL_EXPORTER_OTLP_ENDPOINT: z.string().optional(),
    OTEL_SERVICE_NAMESPACE: z.string().default('ef2c'),
    // Identité légale — jamais inventée (§2.5). Obligatoire hors local.
    LEGAL_COMPANY_NAME: z.string().optional(),
    LEGAL_LEGAL_FORM: z.string().optional(),
    LEGAL_CAPITAL: z.string().optional(),
    LEGAL_ADDRESS: z.string().optional(),
    LEGAL_RC: z.string().optional(),
    LEGAL_ICE: z.string().optional(),
    LEGAL_IF: z.string().optional(),
    LEGAL_CNSS: z.string().optional(),
    LEGAL_PATENTE: z.string().optional(),
    LEGAL_VAT_RATE: z.coerce.number().min(0).max(100).optional(),
    LEGAL_PUBLICATION_MANAGER: z.string().optional(),
    LEGAL_HOSTING_PROVIDER: z.string().optional(),
    LEGAL_PHONE: z.string().optional(),
    LEGAL_CNDP_DECLARATION: z.string().optional(),
})
    .superRefine((env, ctx) => {
    if (env.ENVIRONMENT === 'local')
        return;
    const obligatoiresHorsLocal = [
        'LEGAL_COMPANY_NAME',
        'LEGAL_LEGAL_FORM',
        'LEGAL_ADDRESS',
        'LEGAL_RC',
        'LEGAL_ICE',
        'LEGAL_IF',
        'LEGAL_VAT_RATE',
        'LEGAL_PUBLICATION_MANAGER',
        'LEGAL_HOSTING_PROVIDER',
        'LEGAL_PHONE',
    ];
    for (const cle of obligatoiresHorsLocal) {
        if (env[cle] === undefined || env[cle] === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [cle],
                message: `obligatoire quand ENVIRONMENT=${env.ENVIRONMENT} : ` +
                    'cette valeur apparaît sur les factures et les mentions légales, ' +
                    'elle ne peut pas être approximée (§8)',
            });
        }
    }
    if (env.PAYMENT_PROVIDER !== 'virement') {
        for (const cle of ['PAYMENT_MERCHANT_ID', 'PAYMENT_SECRET_KEY']) {
            if (!env[cle]) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [cle],
                    message: `obligatoire avec PAYMENT_PROVIDER=${env.PAYMENT_PROVIDER}`,
                });
            }
        }
    }
    if (!env.SMTP_PASSWORD) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['SMTP_PASSWORD'],
            message: `obligatoire quand ENVIRONMENT=${env.ENVIRONMENT}`,
        });
    }
});
export class ErreurConfiguration extends Error {
    details;
    constructor(details) {
        super('Configuration invalide — le service ne démarre pas.\n' +
            details.map((d) => `  · ${d}`).join('\n') +
            '\nVoir .env.example : chaque variable y est documentée (rôle, format, obligatoire).');
        this.details = details;
        this.name = 'ErreurConfiguration';
    }
}
function analyser(schema, source) {
    const resultat = schema.safeParse(source);
    if (!resultat.success) {
        throw new ErreurConfiguration(resultat.error.issues.map((i) => `${i.path.join('.') || '(racine)'} : ${i.message}`));
    }
    return resultat.data;
}
/** À appeler tout en haut du démarrage du BFF, avant toute autre initialisation. */
export function chargerEnvServeur(source) {
    return analyser(schemaEnvServeur, source);
}
/** À appeler au démarrage d'un frontend, sur la configuration lue au runtime. */
export function chargerEnvPublic(source) {
    return analyser(schemaEnvPublic, source);
}
/**
 * Extrait de la configuration serveur les seules clés publiables au navigateur.
 * Toute autre voie d'exposition est un défaut de sécurité : si une URL interne
 * atteint un bundle, ce n'est pas un détail (§7 bis.1).
 */
export function extraireEnvPublic(env) {
    return {
        BASE_DOMAIN: env.BASE_DOMAIN,
        ENVIRONMENT: env.ENVIRONMENT,
        DEFAULT_LOCALE: env.DEFAULT_LOCALE,
        SUPPORTED_LOCALES: env.SUPPORTED_LOCALES,
        CURRENCY: env.CURRENCY,
        TIMEZONE: env.TIMEZONE,
        LOG_LEVEL: env.LOG_LEVEL,
        PUBLIC_WEB_URL: env.PUBLIC_WEB_URL,
        PUBLIC_APP_URL: env.PUBLIC_APP_URL,
        PUBLIC_API_URL: env.PUBLIC_API_URL,
        PUBLIC_AUTH_URL: env.PUBLIC_AUTH_URL,
        OIDC_ISSUER_URL: env.OIDC_ISSUER_URL,
        OIDC_REDIRECT_URI: env.OIDC_REDIRECT_URI,
        OIDC_REALM: env.OIDC_REALM,
        OIDC_CLIENT_ID_APP: env.OIDC_CLIENT_ID_APP,
    };
}
//# sourceMappingURL=env.js.map